// MobileDashboard.jsx
// Phone-tier Home screen — the design's "Mobile Native Feel" dashboard:
// greeting → P&L hero → quick actions → live connections → top movers.
// Rebuilt on the DS mobile primitive layer (Phase 2): NumberHero/StatTile for
// every figure, ListRow for the two list sections, monochrome quick actions
// (90/8/2 color rule — signed color lives ONLY on P&L deltas / mover change).
// Theme-aware (DS CSS vars).
//
// NOTE: P&L / movers values here are representative placeholders — the layout
// and data-shape are real; wiring to live portfolio/tick data is a follow-up.
import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { StatTile, NumberHero, ListRow, Icon, TAP, TickFlash, PullToRefresh } from "../ds";

// Placeholder trend series for the P&L hero sparkline (an upward line + area).
const SPARK_UP = [6, 10, 8, 14, 12, 18, 16, 20, 18, 24, 22];

const SESSION_STATS = [
  { k: "Realized", v: "₹8,420" },
  { k: "Unrealized", v: "₹4,427" },
  { k: "Orders", v: "14 / 16" },
];

const QUICK_ACTIONS = [
  { label: "Buy", icon: "gain", to: "/SmartTrade" },
  { label: "Sell", icon: "loss", to: "/SmartTrade" },
  { label: "Watch", icon: "watchlist", to: "/WatchList" },
  { label: "Strategy", icon: "strategy", to: "/Strategies/Modern" },
];

const CONNECTIONS = [
  { name: "Flattrade", sub: "NSE · BSE · F&O", status: "Connected" },
  { name: "ICICI Breeze", sub: "NSE · BSE", status: "Connected" },
];

const MOVERS = [
  { sym: "RELIANCE", ex: "NSE", qty: "100 lots · long", price: "2,847.55", chg: "61.20", pct: "2.20", up: true },
  { sym: "HDFCBANK", ex: "NSE", qty: "50 lots · long", price: "1,624.10", chg: "13.80", pct: "0.86", up: true },
  { sym: "INFY", ex: "NSE", qty: "200 lots · short", price: "1,492.65", chg: "9.45", pct: "0.63", up: false },
  { sym: "TATAMOTOR", ex: "NSE", qty: "80 lots · long", price: "982.30", chg: "14.05", pct: "1.45", up: true },
];

// Section label — small caps/eyebrow muted style, reusing the DS stat-tile
// eyebrow token treatment for consistency across the page.
function SectionLabel({ children }) {
  return <span className="ds-stat-tile-eyebrow">{children}</span>;
}

function SectionLink({ children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ appearance: "none", background: "none", border: "none", cursor: "pointer", fontSize: 11, color: "var(--color-accent)" }}
    >
      {children}
    </button>
  );
}

// Monochrome quick action — icon + label only. bg-2 fill, fg-1 icon. This is
// the LAST place a tinted-pair pattern used to live; now tokens-only.
function QuickAction({ label, icon, onClick }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={TAP}
      style={{
        appearance: "none",
        cursor: "pointer",
        background: "var(--bg-2)",
        border: "none",
        borderRadius: "var(--radius-xl)",
        padding: "14px 6px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        color: "var(--fg-1)",
      }}
    >
      <Icon name={icon} size={18} />
      <span style={{ fontSize: 11, color: "var(--fg-2)", fontWeight: 500 }}>{label}</span>
    </motion.button>
  );
}

// Top-mover row: symbol + exchange chip as title (matches MobileWatchList's
// WatchRow pattern), position line as subtitle, price + signed change trailing.
function MoverRow({ m, last }) {
  const color = m.up ? "var(--color-profit)" : "var(--color-loss)";
  return (
    <ListRow
      style={last ? undefined : { borderBottom: "1px solid var(--border)" }}
      title={
        <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--fg-1)" }}>{m.sym}</span>
          <span
            style={{
              fontSize: 9,
              fontWeight: 600,
              color: "var(--fg-3)",
              border: "1px solid var(--border)",
              padding: "1px 5px",
              borderRadius: 3,
              letterSpacing: ".05em",
              flex: "0 0 auto",
            }}
          >
            {m.ex}
          </span>
        </span>
      }
      subtitle={m.qty}
      trailing={
        // fires on live ticks — TickFlash washes teal/rose whenever the price
        // value changes (sign="auto" compares new vs old); keyed on m.price so
        // each mover's block flashes independently.
        <TickFlash
          value={m.price}
          sign="auto"
          style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, borderRadius: 6, padding: "1px 4px", margin: "-1px -4px" }}
        >
          <span className="ds-mono" style={{ color: "var(--fg-1)" }}>{m.price}</span>
          <span className="ds-mono" style={{ fontSize: 11, color }}>
            {m.up ? "▲" : "▼"} {m.chg} · {m.up ? "+" : "−"}{m.pct}%
          </span>
        </TickFlash>
      }
    />
  );
}

export default function MobileDashboard({ onRefresh }) {
  const navigate = useNavigate();

  // The phone AppShell owns document-level scrolling (no inner overflow
  // container here), so PullToRefresh engages off window scrollTop === 0.
  // onRefresh is threaded from the parent when live data lands; until then a
  // short resolve gives the gesture something real to await.
  const handleRefresh =
    onRefresh || (() => new Promise((res) => setTimeout(res, 600))); // TODO: wire to live portfolio refetch

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div style={{ padding: "0 16px", color: "var(--fg-1)" }}>
      {/* Greeting */}
      <div style={{ marginBottom: 20, paddingTop: 2 }}>
        <SectionLabel>Welcome back, Deepak</SectionLabel>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <span className="ds-mono" style={{ fontSize: 12, color: "var(--fg-2)" }}>Session 2h 14m 07s</span>
          <span style={{ width: 3, height: 3, borderRadius: 2, background: "var(--fg-3)" }} />
          <span style={{ fontSize: 12, color: "var(--color-profit)", fontWeight: 500 }}>
            2 brokers connected
          </span>
        </div>
      </div>

      {/* P&L hero — numbers-as-hero moment: borderless, sits directly on the
          page background. Color lives on the delta chip only. */}
      <div style={{ marginBottom: 24 }}>
        <NumberHero
          eyebrow="Today's P&L"
          value="+₹12,847"
          rolling /* fires on live ticks — each digit rolls as P&L updates */
          delta={{ text: "+2.34%", sign: 1 }}
          sparkline={SPARK_UP}
        >
          <span style={{ fontSize: 11, color: "var(--fg-3)" }}>vs. yesterday</span>
        </NumberHero>
        <div style={{ display: "flex", gap: 24, marginTop: 20 }}>
          {SESSION_STATS.map((s) => (
            <StatTile key={s.k} eyebrow={s.k} value={s.v} size="lg" />
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, marginBottom: 20 }}>
        {QUICK_ACTIONS.map((a) => (
          <QuickAction key={a.label} label={a.label} icon={a.icon} onClick={() => navigate(a.to)} />
        ))}
      </div>

      {/* Live connections */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <SectionLabel>Live Connections</SectionLabel>
          <SectionLink onClick={() => navigate("/TradeAccounts")}>Manage</SectionLink>
        </div>
        <div style={{ background: "var(--bg-2)", borderRadius: "var(--radius-xl)", overflow: "hidden" }}>
          {/* TODO: Skeleton rows when wired to live data */}
          {CONNECTIONS.map((b, i) => (
            <ListRow
              key={b.name}
              style={i === CONNECTIONS.length - 1 ? undefined : { borderBottom: "1px solid var(--border)" }}
              leading={
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    background: "var(--color-profit)",
                    boxShadow: "0 0 0 3px rgb(var(--color-profit-rgb) / 0.25)",
                  }}
                />
              }
              title={b.name}
              subtitle={b.sub}
              trailing={<span style={{ fontSize: 11, color: "var(--fg-2)", fontWeight: 500 }}>{b.status}</span>}
            />
          ))}
        </div>
      </div>

      {/* Top movers */}
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <SectionLabel>Top Movers · NIFTY 50</SectionLabel>
          <SectionLink onClick={() => navigate("/WatchList")}>See all</SectionLink>
        </div>
        <div style={{ background: "var(--bg-2)", borderRadius: "var(--radius-xl)", overflow: "hidden" }}>
          {/* TODO: Skeleton rows when wired to live data */}
          {MOVERS.map((m, i) => (
            <MoverRow key={m.sym} m={m} last={i === MOVERS.length - 1} />
          ))}
        </div>
      </div>
    </div>
    </PullToRefresh>
  );
}
